import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { CodeOutlined, CopyOutlined, EditOutlined, LinkOutlined, ReloadOutlined, TableOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Content } from "antd/es/layout/layout";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import dayjs from "dayjs";
// Importar o gerador também aplica o pt-BR (vírgula decimal) no Highcharts.
import { CHAVE_SPEC_IMPORTADO } from "../Graficos/GeradorGraficos";
import {
  COT_MERCADOS,
  COT_RELATORIOS,
  buscarCot,
  calcularCot,
  dataBR,
  fmtContratos,
  leituraDaSemana,
  zonaCot,
} from "./cot/cotDados";
import {
  COLUNAS_TABELA,
  COT_VISOES,
  htmlDaTabela,
  iframeDoGrafico,
  opcoesCot,
  specCotParaGerador,
  tsvDaTabela,
} from "./cot/cotGraficos";

const { Title, Text, Paragraph } = Typography;

const PERIODOS = [
  { value: 13, label: "3 meses" },
  { value: 26, label: "6 meses" },
  { value: 52, label: "1 ano" },
  { value: 104, label: "2 anos" },
  { value: 156, label: "3 anos" },
];

/**
 * Posição dos Fundos (COT/CFTC): gráficos e tabela da posição dos fundos
 * (Managed Money) nos mercados agrícolas americanos, para as matérias.
 * Cada gráfico tem "Copiar" (código para incorporar, o mesmo que o editor
 * captura em "Gráfico de Relatório") e "Gerador" (vira imagem no padrão da
 * casa).
 */
export default function COT() {
  const navigate = useNavigate();
  const ehPopup = useMemo(() => new URLSearchParams(window.location.search).get("popup") === "1", []);

  const [codigo, setCodigo] = useState(COT_MERCADOS[0].code);
  const [relatorio, setRelatorio] = useState("futuros");
  const [semanas, setSemanas] = useState(52);
  const [visoes, setVisoes] = useState(["liquida", "indice"]);
  const [cor, setCor] = useState("#3DAA73");
  const [linhasTabela, setLinhasTabela] = useState(12);

  const [todas, setTodas] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState(null);
  const [recarregar, setRecarregar] = useState(0);

  const [codigoAberto, setCodigoAberto] = useState("");
  const [copiado, setCopiado] = useState(null);

  const mercado = COT_MERCADOS.find((m) => m.code === codigo);

  useEffect(() => {
    let ativo = true;
    setBuscando(true);
    setErro(null);
    buscarCot(codigo, { relatorio, semanas })
      .then((semanasCot) => ativo && setTodas(calcularCot(semanasCot)))
      .catch((e) => {
        if (!ativo) return;
        setTodas([]);
        setErro(`Não foi possível consultar a CFTC: ${e.message}`);
      })
      .finally(() => ativo && setBuscando(false));
    return () => {
      ativo = false;
    };
  }, [codigo, relatorio, semanas, recarregar]);

  // Período exibido: as últimas N semanas (o resto foi buscado só para o
  // COT Index ter a janela de 3 anos completa).
  const linhas = useMemo(() => todas.slice(-semanas), [todas, semanas]);
  const ultima = linhas[linhas.length - 1];

  const graficos = useMemo(
    () =>
      linhas.length
        ? COT_VISOES.filter((v) => visoes.includes(v.key)).map((v) => ({
            ...v,
            opcoes: opcoesCot(v.key, linhas, { mercado, cor }),
          }))
        : [],
    [linhas, visoes, mercado, cor]
  );

  const leitura = useMemo(() => leituraDaSemana(mercado.label, linhas), [mercado, linhas]);
  const tabela = useMemo(() => linhas.slice(-linhasTabela).reverse(), [linhas, linhasTabela]);

  const copiar = (texto, chave, aviso) =>
    navigator.clipboard
      .writeText(texto)
      .then(() => {
        if (chave) {
          setCopiado(chave);
          setTimeout(() => setCopiado(null), 2000);
        }
        if (aviso) message.success(aviso);
      })
      .catch(() => message.error("Erro ao copiar."));

  const abrirNoGerador = (grafico) => {
    try {
      sessionStorage.setItem(CHAVE_SPEC_IMPORTADO, JSON.stringify(specCotParaGerador(grafico.key, grafico.opcoes)));
    } catch {}
    navigate(`/graficos/gerador${ehPopup ? "?popup=1" : ""}`);
  };

  const botaoCopiar = (chave, texto, rotulo = "Copiar") => (
    <Button
      size="small"
      icon={<CopyOutlined />}
      onClick={() => copiar(texto, chave)}
      style={{
        fontSize: 11,
        background: copiado === chave ? "#27ae60" : undefined,
        borderColor: copiado === chave ? "#27ae60" : undefined,
        color: copiado === chave ? "#fff" : undefined,
      }}
    >
      {copiado === chave ? "Copiado!" : rotulo}
    </Button>
  );

  const colunasAntd = COLUNAS_TABELA.map((c) => ({
    title: c.titulo,
    key: c.key,
    align: c.key === "data" ? "left" : "right",
    render: (_, l) => {
      const texto = c.fmt(l);
      if (c.key === "liquido" || c.key === "liquidoVar") {
        return <span style={{ color: l[c.key] < 0 ? "#c0392b" : c.key === "liquidoVar" && l[c.key] > 0 ? "#1e8449" : undefined }}>{texto}</span>;
      }
      if (c.key === "indice" && l.indice !== null) {
        const z = zonaCot(l.indice);
        return z.cor === "default" ? texto : <Tag color={z.cor} style={{ marginRight: 0 }}>{texto}</Tag>;
      }
      return texto;
    },
  }));

  const nomeRelatorio = COT_RELATORIOS[relatorio].label;

  return (
    <Content style={{ margin: "12px 16px" }}>
      <Title level={3}>Posição dos Fundos (COT/CFTC)</Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6} xl={5}>
          <Card title="Consulta" size="small">
            <Form layout="vertical" size="small">
              <Form.Item label="Mercado">
                <Select
                  value={codigo}
                  onChange={setCodigo}
                  showSearch
                  optionFilterProp="label"
                  options={COT_MERCADOS.map((m) => ({ value: m.code, label: `${m.label} (${m.bolsa})` }))}
                />
              </Form.Item>
              <Form.Item label="Relatório">
                <Radio.Group value={relatorio} onChange={(e) => setRelatorio(e.target.value)} optionType="button" buttonStyle="solid">
                  {Object.entries(COT_RELATORIOS).map(([k, r]) => (
                    <Radio.Button key={k} value={k}>
                      {r.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>
              <Form.Item label="Período">
                <Select value={semanas} onChange={setSemanas} options={PERIODOS} />
              </Form.Item>
              <Form.Item label="Gráficos">
                <Checkbox.Group
                  value={visoes}
                  onChange={setVisoes}
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  options={COT_VISOES.map((v) => ({ value: v.key, label: v.label }))}
                />
              </Form.Item>
              <Form.Item label="Cor principal">
                <Space>
                  <input
                    type="color"
                    value={cor}
                    onChange={(e) => setCor(e.target.value)}
                    style={{ width: 40, height: 28, padding: 2, cursor: "pointer", borderRadius: 4, border: "1px solid #d9d9d9" }}
                  />
                  <Button size="small" onClick={() => setCor("#3DAA73")}>
                    Reset
                  </Button>
                </Space>
              </Form.Item>
              <Button block icon={<ReloadOutlined />} loading={buscando} onClick={() => setRecarregar((n) => n + 1)}>
                Atualizar dados
              </Button>
              <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 12, marginBottom: 0 }}>
                Fundos = <i>Managed Money</i> do COT <i>Disaggregated</i>. Posição de terça-feira, divulgada pela CFTC na
                sexta seguinte.{" "}
                <a href="https://www.cftc.gov/MarketReports/CommitmentsofTraders/index.htm" target="_blank" rel="noreferrer">
                  <LinkOutlined /> CFTC
                </a>
              </Paragraph>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={18} xl={19}>
          {erro && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={erro} />}
          {buscando && !linhas.length ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <Spin size="large" />
            </div>
          ) : ultima ? (
            <Spin spinning={buscando}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={`${mercado.label} (${mercado.bolsa}) — posição de terça-feira, ${dataBR(ultima.data)}, divulgada pela CFTC na sexta seguinte. ${nomeRelatorio}.`}
                description={`Próxima posição: terça ${dataBR(dayjs(ultima.data).add(7, "day"))}, divulgada na sexta seguinte (em semana de feriado nos EUA, na segunda).`}
              />

              <Card
                size="small"
                title="Leitura da semana"
                style={{ marginBottom: 12 }}
                extra={botaoCopiar("leitura", leitura.join(" "), "Copiar texto")}
              >
                <Space size={[16, 8]} wrap style={{ marginBottom: 8 }}>
                  <Text>
                    Líquido dos fundos: <Text strong>{fmtContratos(ultima.liquido)}</Text>
                  </Text>
                  <Text>
                    Na semana:{" "}
                    <Text strong style={{ color: ultima.liquidoVar < 0 ? "#c0392b" : "#1e8449" }}>
                      {fmtContratos(ultima.liquidoVar, true)}
                    </Text>
                  </Text>
                  <Text>
                    Comprados: <Text strong>{fmtContratos(ultima.mmLong)}</Text> · Vendidos: <Text strong>{fmtContratos(ultima.mmShort)}</Text>
                  </Text>
                  {ultima.indice !== null && (
                    <Text>
                      COT Index: <Tag color={zonaCot(ultima.indice).cor}>{Math.round(ultima.indice)} · {zonaCot(ultima.indice).texto}</Tag>
                    </Text>
                  )}
                </Space>
                <Paragraph style={{ marginBottom: 0 }}>{leitura.join(" ")}</Paragraph>
              </Card>

              {graficos.length > 0 && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <Button
                    icon={<CodeOutlined />}
                    onClick={() => setCodigoAberto(graficos.map((g) => iframeDoGrafico(g.opcoes)).join("<br>\n"))}
                  >
                    Código de todos os gráficos
                  </Button>
                </div>
              )}
              <Row gutter={[12, 12]}>
                {graficos.map((g) => (
                  <Col key={g.key} xs={24} xl={12}>
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 4 }}>
                      <Button size="small" icon={<EditOutlined />} onClick={() => abrirNoGerador(g)} style={{ fontSize: 11 }}>
                        Gerador
                      </Button>
                      {botaoCopiar(g.key, iframeDoGrafico(g.opcoes))}
                    </div>
                    <HighchartsReact highcharts={Highcharts} options={g.opcoes} />
                  </Col>
                ))}
              </Row>

              <Card
                size="small"
                style={{ marginTop: 12 }}
                title={
                  <Space>
                    <TableOutlined /> Tabela
                    <Select
                      size="small"
                      value={linhasTabela}
                      onChange={setLinhasTabela}
                      options={[4, 8, 12, 26, 52].map((n) => ({ value: n, label: `últimas ${n} semanas` }))}
                    />
                  </Space>
                }
                extra={
                  <Space>
                    {botaoCopiar("tabela", htmlDaTabela(tabela, { mercado, relatorio: nomeRelatorio }), "Copiar tabela (HTML)")}
                    {botaoCopiar("excel", tsvDaTabela(tabela), "Copiar para Excel")}
                  </Space>
                }
              >
                <Table
                  size="small"
                  rowKey="data"
                  columns={colunasAntd}
                  dataSource={tabela}
                  pagination={false}
                  scroll={{ x: 900 }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Em contratos. Fundos = Managed Money. COT Index: posição líquida atual na escala do mínimo (0) ao máximo
                  (100) dos últimos 3 anos; acima de 90 ou abaixo de 10 indica extremo.
                </Text>
              </Card>
            </Spin>
          ) : (
            !erro && (
              <Card size="small">
                <Text type="secondary">Nenhum dado da CFTC para este mercado.</Text>
              </Card>
            )
          )}
        </Col>
      </Row>

      <Modal
        title="Código HTML para incorporar"
        open={!!codigoAberto}
        onCancel={() => setCodigoAberto("")}
        width={800}
        footer={
          <Button type="primary" icon={<CopyOutlined />} onClick={() => copiar(codigoAberto, null, "Código copiado!")}>
            Copiar código
          </Button>
        }
      >
        <Input.TextArea value={codigoAberto} autoSize={{ minRows: 8, maxRows: 20 }} readOnly style={{ fontFamily: "monospace", fontSize: 11 }} />
      </Modal>
    </Content>
  );
}
